<template>
  <v-content>
    <AppBar title="Fullt tåg registreringar" />
    <v-container :fluid="true">
      <v-card>
        <v-data-table
          :footer-props="footerProps"
          :loading="$apollo.loading"
          :headers="headers"
          :items="transformedTrainRegistrations"
          :search="search"
          loading-text="Hämtar data..."
          no-data="Ingen data hittades..."
          no-results-text="Inget resultat på din sökning..."
          :height="tableHeight"
          :items-per-page="20"
          fixed-header
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>Fullt tåg registreringar</v-toolbar-title>
              <v-divider class="mx-4" inset vertical></v-divider>
              <v-spacer></v-spacer>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Sök..."
                single-line
                hide-details
                filled
                dense
              ></v-text-field>
              <v-divider class="mx-4" inset vertical></v-divider>
              <v-spacer />
              <v-dialog v-model="showExportModal" width="500">
                <template v-slot:activator="{ on }">
                  <v-btn tile v-on="on" :loading="pendingExport">
                    <v-icon small left>mdi-file-export-outline</v-icon>
                    Export Excel
                  </v-btn>
                </template>
                <ExportModal
                  v-if="showExportModal"
                  @close="showExportModal = false"
                  @confirm="exportToExcel"
                />
              </v-dialog>
            </v-toolbar>
          </template>

          <template v-slot:item.actions="{ item }">
            <CrowdedTrainRegistrationActionMenu
              :item="item"
              @refetch="refetch"
            />
          </template>
        </v-data-table>
      </v-card>
    </v-container>
  </v-content>
</template>

<script>
import AppBar from '@/components/AppBar.vue'
import tableHeight from '@/mixins/tableHeight.js'
import gql from 'graphql-tag'
import { format } from 'date-fns'
import { exportExcel } from '@/services/exportExcel.service.js'
import ExportModal from '@/components/ExportModal'
import { isWithinInterval } from 'date-fns'
import orderBy from 'lodash.orderby'
import { get } from 'vuex-pathify'
import CrowdedTrainRegistrationActionMenu from './CrowdedTrainRegistrationActionMenu'

export default {
  name: 'CrowdedTrainRegistrations',

  mixins: [tableHeight],

  components: {
    AppBar,
    ExportModal,
    CrowdedTrainRegistrationActionMenu
  },

  data: () => ({
    showMenu: false,
    showUpdateModal: false,
    showExportModal: false,
    pendingExport: false,
    footerProps: {
      itemsPerPageOptions: [10, 20, 50],
      itemsPerPageText: 'Rader per sida'
    },
    search: ''
  }),

  apollo: {
    crowdedTrainRegistrations: {
      fetchPolicy: 'cache-and-network',
      query: gql`
        query {
          crowdedTrainRegistrations {
            id
            train_number
            created
            date
            station
            position
            definition_text
            comment
            canceled
            reduced
            user
            comment_admin
          }
        }
      `
    }
  },

  computed: {
    isSj: get('user/isSj'),

    hasAccess: get('user/hasAccess'),

    transformedTrainRegistrations() {
      const items = (this.crowdedTrainRegistrations || []).map(x => ({
        ...x,
        canceled: x.canceled ? 'Ja' : 'Nej',
        reduced: x.canceled ? 'Ja' : 'Nej',
        date: format(new Date(x.date), 'yyyy-MM-dd'),
        created: format(new Date(x.created), 'yyyy-MM-dd HH:mm')
      }))

      return orderBy(items, ['date', 'created'], ['desc', 'desc'])
    },

    headers() {
      return [
        {
          text: 'Datum',
          value: 'date',
          width: 150
        },
        {
          text: 'Skapad',
          value: 'created',
          width: 200
        },
        {
          text: 'Tågnr.',
          value: 'train_number'
        },
        {
          text: 'Station',
          value: 'station'
        },
        {
          text: 'Påstående',
          value: 'definition_text'
        },
        {
          text: 'Föregående tåg inställd',
          value: 'canceled'
        },
        {
          text: 'Indragen vagn',
          value: 'reduced'
        },
        {
          text: 'Vagn tjänstegörning',
          value: 'position'
        },
        {
          text: 'Kommentar',
          value: 'comment'
        },
        {
          text: 'Skapad av',
          value: 'user'
        },
        {
          text: 'Kommentar admin',
          value: 'comment_admin'
        },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ]
        .filter(header => (!this.isSj ? header.value !== 'user' : true))
        .filter(header =>
          header.value === 'comment_admin'
            ? this.hasAccess(['ADMIN_CROWDED_TRAIN_EDIT_COMMENT'])
            : true
        )
    }
  },

  methods: {
    async exportToExcel(period) {
      const filteredTrainRegistrations = orderBy(
        this.transformedTrainRegistrations,
        ['date', 'created'],
        ['desc', 'desc']
      ).filter(x => {
        return isWithinInterval(new Date(x.date), {
          start: new Date(period.from),
          end: new Date(period.to)
        })
      })
      this.pendingExport = true
      const filename = `Export - Fullt tåg registreringar`
      const workbook = {
        name: filename,
        sheets: [
          {
            name: 'Fullt tåg registreringar',
            data: filteredTrainRegistrations.map(row => {
              return this.headers.reduce((acc, col) => {
                acc[col.text] = row[col.value]
                return acc
              }, {})
            })
          }
        ]
      }

      await exportExcel(this.$apollo.provider.defaultClient, filename, workbook)
      this.pendingExport = false
    },

    refetch() {
      this.$apollo.queries.crowdedTrainRegistrations.refetch()
    }
  }
}
</script>
